import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthPage({ mode }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister && !agreed) {
      setErr("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setBusy(true); setErr("");
    try {
      if (isRegister) {
        await register(email, password);
        toast("Welcome to GP Picks", { description: "Now subscribe to unlock all picks." });
        nav("/paywall", { replace: true });
      } else {
        await login(email, password);
        toast("Welcome back");
        nav(from, { replace: true });
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
      <form onSubmit={submit} className="w-full max-w-md" data-testid={isRegister ? "register-form" : "login-form"}>
        <div className="flex items-center gap-2 mb-8">
          <Lightning size={22} weight="fill" color="#FF4500" />
          <span className="font-head text-xl text-white tracking-tight">GP<span style={{ color: "#FF4500" }}> PICKS</span></span>
        </div>
        <div className="overline mb-2">{isRegister ? "Create Account" : "Sign In"}</div>
        <h1 className="font-head text-4xl text-white tracking-tight mb-6">
          {isRegister ? "START GETTING PICKS." : "WELCOME BACK."}
        </h1>

        <label className="overline block mb-1">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-transparent border border-[#27272A] px-3 h-11 mb-4 text-white outline-none focus:border-[#FF4500] font-[Manrope]"
          data-testid="auth-email"
        />

        <label className="overline block mb-1">Password</label>
        <input
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent border border-[#27272A] px-3 h-11 mb-2 text-white outline-none focus:border-[#FF4500] font-[Manrope]"
          data-testid="auth-password"
        />
        <div className="text-xs text-[#52525b] mb-6">Min 6 characters.</div>

        {isRegister && (
          <label className="flex items-start gap-2 mb-5 text-xs text-[#A1A1AA] cursor-pointer" data-testid="tos-agree-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 accent-[#FF4500]"
              data-testid="tos-agree-checkbox"
            />
            <span>
              I confirm I am 18+ and agree to the{" "}
              <Link to="/terms" target="_blank" className="text-[#FF4500] hover:underline" data-testid="tos-link">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" target="_blank" className="text-[#FF4500] hover:underline" data-testid="privacy-link">Privacy Policy</Link>.
              GP Picks is for informational purposes only, not a sportsbook.
            </span>
          </label>
        )}

        {err && (
          <div className="border border-[#ef4444] text-[#ef4444] text-sm px-3 py-2 mb-4" data-testid="auth-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full h-11 pick-over font-head uppercase tracking-wider text-base disabled:opacity-60"
          data-testid="auth-submit"
        >
          {busy ? "…" : isRegister ? "Create account" : "Sign in"}
        </button>

        <div className="mt-6 text-sm text-[#A1A1AA]">
          {isRegister ? (
            <>Have an account? <Link to="/login" className="text-[#FF4500] hover:underline" data-testid="switch-login">Sign in</Link></>
          ) : (
            <>New here? <Link to="/register" className="text-[#FF4500] hover:underline" data-testid="switch-register">Create an account</Link></>
          )}
        </div>
      </form>
    </div>
  );
}

export function Login() { return <AuthPage mode="login" />; }
export function Register() { return <AuthPage mode="register" />; }
